import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Package,
  Sparkles,
  Target,
  Scale,
  Wrench,
  ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

// Category definitions matching the auto-categorization script
const CATEGORY_DEFINITIONS = {
  'sector_tech': {
    name: 'Sector Technologies & Attributes',
    icon: Wrench,
    description: 'Technical attributes & innovations',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    borderColor: 'border-blue-200 dark:border-blue-800'
  },
  'product_categories': {
    name: 'Product Categories',
    icon: Package,
    description: 'Product types and definitions',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    borderColor: 'border-purple-200 dark:border-purple-800'
  },
  'benefits': {
    name: 'Perceived Benefits',
    icon: Sparkles,
    description: 'Customer benefits and value propositions',
    color: 'text-green-600 dark:text-green-400',
    bgColor: 'bg-green-50 dark:bg-green-950/30',
    borderColor: 'border-green-200 dark:border-green-800'
  },
  'use_cases': {
    name: 'Use Cases & Applications',
    icon: Target,
    description: 'Usage scenarios and applications',
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-950/30',
    borderColor: 'border-orange-200 dark:border-orange-800'
  },
  'comparison_topics': {
    name: 'Comparison Topics',
    icon: Scale,
    description: 'Product and feature comparisons',
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-950/30',
    borderColor: 'border-indigo-200 dark:border-indigo-800'
  }
};

interface QuestionWheel {
  id: string;
  name: string;
  wheel_type: string;
  question_count: number;
  metadata?: {
    categories?: string[];
  };
  created_at: string;
}

interface CategoryGroup {
  category: string;
  wheels: QuestionWheel[];
}

interface QuestionWheelCategoryCardsProps {
  wheels: QuestionWheel[];
  onCategoryClick?: (category: string) => void;
}

export function QuestionWheelCategoryCards({ wheels, onCategoryClick }: QuestionWheelCategoryCardsProps) {
  // Group wheels by category
  const categorizedWheels = React.useMemo(() => {
    const groups: Record<string, QuestionWheel[]> = {};

    wheels.forEach(wheel => {
      const categories = wheel.metadata?.categories || [];

      if (categories.length === 0) {
        // Uncategorized wheels
        if (!groups['uncategorized']) {
          groups['uncategorized'] = [];
        }
        groups['uncategorized'].push(wheel);
      } else {
        // Add to each category the wheel belongs to
        categories.forEach(category => {
          if (!groups[category]) {
            groups[category] = [];
          }
          groups[category].push(wheel);
        });
      }
    });

    return groups;
  }, [wheels]);

  // Calculate total questions per category
  const getCategoryStats = (category: string) => {
    const categoryWheels = categorizedWheels[category] || [];
    const totalQuestions = categoryWheels.reduce(
      (sum, wheel) => sum + (wheel.question_count || 0),
      0
    );
    return {
      wheelCount: categoryWheels.length,
      totalQuestions
    };
  };

  const handleCategoryClick = (category: string) => {
    if (onCategoryClick) {
      onCategoryClick(category);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {Object.keys(CATEGORY_DEFINITIONS).map(categoryKey => {
        const def = CATEGORY_DEFINITIONS[categoryKey as keyof typeof CATEGORY_DEFINITIONS];
        const stats = getCategoryStats(categoryKey);
        const Icon = def.icon;

        // Only show category if it has wheels
        if (stats.wheelCount === 0) return null;

        return (
          <Card
            key={categoryKey}
            className={`hover:shadow-lg transition-all duration-300 cursor-pointer ${def.borderColor} border-2`}
            onClick={() => handleCategoryClick(categoryKey)}
          >
            <CardHeader className={`${def.bgColor} pb-4`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg ${def.bgColor} flex items-center justify-center`}>
                    <Icon className={`h-6 w-6 ${def.color}`} />
                  </div>
                  <div>
                    <CardTitle className="text-lg">{def.name}</CardTitle>
                    <CardDescription className="text-sm mt-1">
                      {def.description}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-2xl font-bold">{stats.wheelCount}</div>
                      <div className="text-xs text-muted-foreground">
                        {stats.wheelCount === 1 ? 'Wheel' : 'Wheels'}
                      </div>
                    </div>
                    <div className="h-8 w-px bg-border" />
                    <div>
                      <div className="text-2xl font-bold">{stats.totalQuestions.toLocaleString()}</div>
                      <div className="text-xs text-muted-foreground">Questions</div>
                    </div>
                  </div>
                </div>

                <Button
                  variant="outline"
                  className="w-full mt-4 group"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCategoryClick(categoryKey);
                  }}
                >
                  View Wheels
                  <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </CardContent>
          </Card>
        );
      })}

      {/* Uncategorized wheels card if any exist */}
      {categorizedWheels['uncategorized'] && categorizedWheels['uncategorized'].length > 0 && (
        <Card className="hover:shadow-lg transition-all duration-300 cursor-pointer border-2 border-gray-200 dark:border-gray-700">
          <CardHeader className="bg-gray-50 dark:bg-gray-900/30 pb-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gray-50 dark:bg-gray-900/30 flex items-center justify-center">
                  <Package className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                </div>
                <div>
                  <CardTitle className="text-lg">Uncategorized</CardTitle>
                  <CardDescription className="text-sm mt-1">
                    Wheels awaiting categorization
                  </CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div>
                    <div className="text-2xl font-bold">{categorizedWheels['uncategorized'].length}</div>
                    <div className="text-xs text-muted-foreground">
                      {categorizedWheels['uncategorized'].length === 1 ? 'Wheel' : 'Wheels'}
                    </div>
                  </div>
                </div>
              </div>

              <Button
                variant="outline"
                className="w-full mt-4 group"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCategoryClick('uncategorized');
                }}
              >
                View Wheels
                <ChevronRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
